import * as React from "react"

// 3-tier responsive breakpoint system
const SIDEBAR_COLLAPSE_BREAKPOINT = 1280  // Point 1: Auto-collapse sidebar (system-wide)
const CHAT_HISTORY_COLLAPSE_BREAKPOINT = 1024  // Point 2: Auto-collapse chat history
const MOBILE_BREAKPOINT = 768  // Point 3: Mobile view trigger
const SMALL_MOBILE_BREAKPOINT = 640
const TINY_MOBILE_BREAKPOINT = 480

export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean>(() => {
    // Initialize with actual window size if available
    if (typeof window !== 'undefined') {
      return window.innerWidth < MOBILE_BREAKPOINT;
    }
    return false;
  });

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }
    mql.addEventListener("change", onChange)
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    return () => mql.removeEventListener("change", onChange)
  }, [])

  return isMobile
}

export function useResponsiveBreakpoints() {
  const [breakpoints, setBreakpoints] = React.useState(() => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      return {
        shouldCollapseSidebar: width < SIDEBAR_COLLAPSE_BREAKPOINT,
        shouldCollapseChatHistory: width < CHAT_HISTORY_COLLAPSE_BREAKPOINT,
        isMobile: width < MOBILE_BREAKPOINT,
        isTablet: width >= MOBILE_BREAKPOINT && width < SIDEBAR_COLLAPSE_BREAKPOINT,
        isSmallMobile: width < SMALL_MOBILE_BREAKPOINT,
        isTinyMobile: width < TINY_MOBILE_BREAKPOINT,
        width
      };
    }
    return {
      shouldCollapseSidebar: false,
      shouldCollapseChatHistory: false,
      isMobile: false,
      isTablet: false,
      isSmallMobile: false, 
      isTinyMobile: false,
      width: 1280
    };
  });

  React.useEffect(() => {
    const updateBreakpoints = () => {
      const width = window.innerWidth;
      setBreakpoints({
        shouldCollapseSidebar: width < SIDEBAR_COLLAPSE_BREAKPOINT,
        shouldCollapseChatHistory: width < CHAT_HISTORY_COLLAPSE_BREAKPOINT,
        isMobile: width < MOBILE_BREAKPOINT,
        isTablet: width >= MOBILE_BREAKPOINT && width < SIDEBAR_COLLAPSE_BREAKPOINT,
        isSmallMobile: width < SMALL_MOBILE_BREAKPOINT,
        isTinyMobile: width < TINY_MOBILE_BREAKPOINT,
        width
      });
    };

    updateBreakpoints();
    window.addEventListener('resize', updateBreakpoints);
    return () => window.removeEventListener('resize', updateBreakpoints);
  }, []);

  return breakpoints;
}

// Hook for system-wide sidebar auto-collapse
export function useSidebarResponsive() {
  const [shouldCollapse, setShouldCollapse] = React.useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < SIDEBAR_COLLAPSE_BREAKPOINT;
    }
    return false;
  });

  React.useEffect(() => {
    const updateSidebarState = () => {
      setShouldCollapse(window.innerWidth < SIDEBAR_COLLAPSE_BREAKPOINT);
    };

    updateSidebarState();
    window.addEventListener('resize', updateSidebarState);
    return () => window.removeEventListener('resize', updateSidebarState);
  }, []);

  return shouldCollapse;
}

// Hook for chat history auto-collapse
export function useChatHistoryResponsive() {
  const [shouldCollapse, setShouldCollapse] = React.useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < CHAT_HISTORY_COLLAPSE_BREAKPOINT;
    }
    return false;
  });

  React.useEffect(() => {
    const updateChatHistoryState = () => {
      setShouldCollapse(window.innerWidth < CHAT_HISTORY_COLLAPSE_BREAKPOINT);
    };

    updateChatHistoryState();
    window.addEventListener('resize', updateChatHistoryState);
    return () => window.removeEventListener('resize', updateChatHistoryState);
  }, []);

  return shouldCollapse;
}
